/**
 * ALTERAÇÕES NÃO SALVAS, em Configurações.
 *
 * Apontamento 14 da revisão de UX: "um 'Salvar' por seção — fácil editar
 * duas seções e salvar só uma". Está certo, e o custo é concreto: quem mexe
 * na assinatura e na resposta automática, salva uma e sai, perde a outra sem
 * nada dizer — a tela some e as duas pareciam ter sido guardadas.
 *
 * DOS DOIS AJUSTES PEDIDOS — "avisar alterações não salvas ou salvar
 * automaticamente" — este é o avisar, e a escolha é deliberada: salvar
 * sozinho aqui publicaria um Sieve a cada tecla digitada num filtro (é o que
 * `salvarFiltros` faz: compila e publica no Dovecot), e ligaria uma resposta
 * automática de férias no meio da frase. Rascunho de mensagem salva sozinho
 * porque é rascunho; configuração que entra em vigor não.
 *
 * E UM "SALVAR" SÓ PARA A TELA TAMBÉM NÃO SERVE: as seções falam com rotas
 * diferentes, e um botão único teria que decidir o que fazer quando a
 * terceira chamada falha depois de as duas primeiras passarem. O que falta
 * não é um botão, é a tela dizer o que ainda não foi guardado.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

type Registro = Record<string, boolean>;

const Contexto = createContext<{
  pendentes: string[];
  registrar: (secao: string, sujo: boolean) => void;
}>({ pendentes: [], registrar: () => {} });

export function ProvedorDeNaoSalvo({ children }: { children: ReactNode }) {
  const [registro, setRegistro] = useState<Registro>({});

  const pendentes = useMemo(
    () => Object.entries(registro)
      .filter(([, sujo]) => sujo)
      .map(([secao]) => secao)
      .sort(),
    [registro],
  );

  /**
   * O AVISO DO NAVEGADOR, para fechar a aba e recarregar.
   *
   * É a única saída que a tela não controla — um `<Link>` daqui dá para
   * interceptar, fechar a aba não. O texto é escolhido pelo navegador: desde
   * 2016 ninguém aceita mensagem própria aqui, justamente porque era usada
   * para prender a pessoa na página.
   *
   * Só registra o ouvinte quando há o que perder: um `beforeunload` sempre
   * ativo impede o navegador de guardar a página no cache de navegação, e
   * voltar para a aba fica mais lento sem nenhum motivo.
   */
  useEffect(() => {
    if (pendentes.length === 0) return;
    function aoSair(e: BeforeUnloadEvent) {
      e.preventDefault();
      // `returnValue` continua necessário para navegadores antigos; os atuais
      // se contentam com o preventDefault.
      e.returnValue = "";
    }
    window.addEventListener("beforeunload", aoSair);
    return () => window.removeEventListener("beforeunload", aoSair);
  }, [pendentes.length]);

  const valor = useMemo(
    () => ({
      pendentes,
      registrar: (secao: string, sujo: boolean) =>
        setRegistro((a) => (a[secao] === sujo ? a : { ...a, [secao]: sujo })),
    }),
    [pendentes],
  );

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

/**
 * Declara que esta seção tem (ou não tem) alteração pendente.
 *
 * `sujo` é comparação de VALOR com o que está guardado, não um "a pessoa
 * digitou aqui". A diferença importa: quem digita e desfaz voltaria a ter um
 * aviso permanente de que há algo a salvar, e aviso que não some é aviso que
 * se aprende a ignorar — e aí ele não serve para o caso em que importa.
 */
export function useNaoSalvo(secao: string, sujo: boolean) {
  const { registrar } = useContext(Contexto);
  useEffect(() => {
    registrar(secao, sujo);
    // Ao desmontar, a seção deixa de ter pendência: trocar de tela com uma
    // seção suja não pode deixar o aviso pendurado para sempre.
    return () => registrar(secao, false);
  }, [secao, sujo, registrar]);
  return sujo;
}

/** Quais seções têm alteração pendente. Para a faixa de aviso e a saída. */
export function useNaoSalvos() {
  return useContext(Contexto).pendentes;
}
